// Политика повторов транспорта — общая для COM-порта (T-033) и Bluetooth Classic
// SPP (T-030). Урок B-023: очередь синхронизации замолчала навсегда именно потому,
// что у транспорта не было потолка ожидания — зависший сокет держит вызывающего
// вечно. Оба транспорта решают её одинаково, поэтому политика здесь одна, а не
// продублирована в каждом файле: разъехавшиеся числа (таймаут, backoff, число
// попыток) — источник багов, которые не ловит ни один тест, кроме сравнения файлов.
//
// Числа замерены на T-035 (спайк) и рассчитаны так, чтобы уложиться в критерий
// «выключенный принтер даёт ошибку не позже чем через 20 с»: 3·5 + 0.5 + 1 + 2 = 18,5.

export const LABEL_TIMEOUT_MS = 5000;
export const RECONNECT_BACKOFF_MS = [500, 1000, 2000] as const;
export const MAX_ATTEMPTS = 3;

// Потолок на САМО подключение (connect/open), отдельно от `LABEL_TIMEOUT_MS`.
// Зачем разделять: зависший `connect()` — это «принтер занят другим устройством»
// (SPP-слот держит другое устройство, найдено на живом железе 2026-09-05), а
// зависший `write()` — это «принтер выключили посреди печати». Причины разные,
// сообщения повару разные, поэтому таймаут на connect ловится отдельно и раньше.
export const CONNECT_TIMEOUT_MS = 4000;

/** Промис не уложился в отведённое время. Отдельный тип — чтобы вызывающий отличил
 * таймаут (принтер молчит) от бро­шенной ошибки (сокет мёртв, отказ в доступе). */
export class TimeoutError extends Error {
  constructor(ms: number) {
    super(`превышено ${ms} мс`);
    this.name = 'TimeoutError';
  }
}

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function withTimeout<T>(op: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    op,
    new Promise<T>((_, reject) => setTimeout(() => reject(new TimeoutError(ms)), ms)),
  ]);
}

/**
 * Повторить попытку до `MAX_ATTEMPTS` раз с паузами `RECONNECT_BACKOFF_MS` и
 * потолком `LABEL_TIMEOUT_MS` на каждую. `onAttemptFailed` — сбросить состояние
 * соединения между попытками (закрыть порт/сокет), чтобы следующая пробовала
 * заново, а не билась в то же мёртвое соединение.
 */
export async function sendWithRetry(
  attempt: () => Promise<unknown>,
  onAttemptFailed: () => Promise<unknown>,
): Promise<void> {
  let lastError: unknown;
  for (let i = 0; i < MAX_ATTEMPTS; i += 1) {
    if (i > 0) {
      await sleep(RECONNECT_BACKOFF_MS[i - 1] ?? 2000);
    }
    try {
      await withTimeout(attempt(), LABEL_TIMEOUT_MS);
      return;
    } catch (error) {
      lastError = error;
      await onAttemptFailed().catch(() => {});
    }
  }
  throw lastError;
}
